import React, {useCallback, useEffect, useState} from 'react';
import {Menu} from 'shared/components/HeaderMenu/Menu';
import {isMobile} from 'react-device-detect';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {Dropdown} from 'react-bootstrap';
import {Icon} from 'shared/helpers';
import styled, {css} from 'styled-components';
import {Menu as MenuInterface} from 'config/entity/menu/HeaderMenu';

interface WithLoadingProps {
    topLevelMenu: MenuInterface;
    children: React.ReactNode;
}

export const MenuWithSubmenu = ({topLevelMenu, children}: WithLoadingProps) => {
    const isSmallDevice = isDeviceSmall();
    const [isSubmenuVisible, setIsSubmenuVisible] = useState(false);

    const toggleMenu = useCallback(
        (state: boolean) => () => {
            setIsSubmenuVisible(state);
        },
        []
    );

    const inlineSubmenu = isMobile || isSmallDevice;

    const toggleMenuOnClick = useCallback(
        (event: React.MouseEvent) => {
            if (!inlineSubmenu) return;

            event.preventDefault();
            event.stopPropagation();
            setIsSubmenuVisible((open) => !open);
        },
        [inlineSubmenu]
    );

    const hideSubMenu = useCallback((event: MouseEvent) => {
        const mainMenu = (event.target as HTMLElement).closest('.main-menu');

        if (!mainMenu) {
            setIsSubmenuVisible(false);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('click', hideSubMenu);

        return () => {
            document.removeEventListener('click', hideSubMenu);
        };
    }, []);

    if (
        topLevelMenu.hasOwnProperty('submenu') &&
        typeof topLevelMenu.submenu !== 'undefined' &&
        topLevelMenu.submenu.length > 0
    ) {
        return (
            <Item as="div" className="dropdown-item submenu">
                <Dropdown
                    show={isSubmenuVisible}
                    drop={inlineSubmenu ? 'down' : 'start'}
                    onMouseEnter={inlineSubmenu ? undefined : toggleMenu(true)}
                    onMouseLeave={
                        inlineSubmenu ? undefined : toggleMenu(false)
                    }>
                    <Dropdown.Toggle
                        onClick={toggleMenuOnClick}
                        role="submenu-toggle">
                        <Icon iconName={topLevelMenu.icon} />
                        {topLevelMenu.name}
                    </Dropdown.Toggle>
                    <DropdownMenu
                        $inline={inlineSubmenu}
                        menu={topLevelMenu.submenu}
                    />
                </Dropdown>
            </Item>
        );
    }

    return children;
};

const DropdownMenu = styled(
    ({...props}: {$inline?: boolean} & React.ComponentProps<typeof Menu>) => (
        <Menu {...props} />
    )
)<{$inline?: boolean}>`
    position: absolute !important;
    inset: 0 !important;
    transform: translateY(0) translateX(-100%) !important;

    ${({$inline}) =>
        $inline &&
        css`
            position: static !important;
            inset: auto !important;
            transform: none !important;
            width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 0 2px !important;
            float: none !important;
            background: transparent !important;
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;

            .dropdown-item {
                margin: 0;
                padding: 9px 12px 9px 34px !important;
                font-size: 0.8em;
                border-radius: 0 !important;
                border-left: 3px solid rgb(var(--secondary_colour));
                background: rgba(0, 0, 0, 0.28) !important;
            }
        `}
`;

const Item = styled(Dropdown.Item)`
    padding: 0 !important;

    > div > button {
        border: 0 !important;
        background: inherit !important;
        font-size: 1em !important;

        > img {
            width: 20px;
            height: 20px;
        }
    }
`;
